import React, { useState, useEffect } from "react"
import moment from "moment"
import getAppConfig from "~/utils/getAppConfig"
import { apiWorkspaceActivities } from "~/routes"
import LoadingWrapper from "~/library/LoadingWrapper"
import type Activity from "~/types/Activity"
import { startCase } from "lodash"

interface ActivityFeedProps {
    itemType: string
    itemId: string
}

interface PagyData {
    page: number
    pages: number
    count: number
    next: number | null
    prev: number | null
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ itemType, itemId }) => {
    const { venueSlug } = getAppConfig()
    const [currentPage, setCurrentPage] = useState(1)
    const [activities, setActivities] = useState<Activity[]>([])
    const [pagy, setPagy] = useState<PagyData | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        fetchActivities()
    }, [itemType, itemId, currentPage])

    const fetchActivities = async () => {
        setIsLoading(true)
        try {
            const endpoint = apiWorkspaceActivities.index.path({ venue_slug: venueSlug })
            const params = new URLSearchParams()

            if (itemType) params.append('item_type', itemType)
            if (itemId) params.append('item_id', itemId)
            params.append('page', currentPage.toString())

            const response = await fetch(`${endpoint}?${params.toString()}`)
            const data = await response.json()

            setActivities(data.activities || [])

            if (data.metadata) {
                setPagy({
                    page: data.metadata.page,
                    pages: data.metadata.pages,
                    count: data.metadata.count,
                    next: data.metadata.next,
                    prev: data.metadata.prev
                })
            }
        } catch (error) {
            console.error("Error fetching activities:", error)
        } finally {
            setIsLoading(false)
        }
    }

    const getEventDescription = (activity: Activity) => {
        switch (activity.event) {
            case 'create':
                return 'created'
            case 'update':
                return 'updated'
            case 'stage_changed':
                return 'changed stage for'
            case 'destroy':
                return 'deleted'
            default:
                return activity.event
        }
    }

    const formatChanges = (changes: Record<string, [string, string]>, event: string) => {
        return Object.entries(changes).map(([field, [from, to]]) => {
            const fieldName = startCase(field)

            if (event === 'create') {
                return (
                    <li key={field} className="font-13">
                        <strong>{fieldName}</strong>: {startCase(to) || '(empty)'}
                    </li>
                )
            }

            return (
                <li key={field} className="font-13">
                    <strong>{fieldName}</strong>: "{startCase(from) || '(empty)'}" → "{startCase(to) || '(empty)'}"
                </li>
            )
        })
    }

    if (!activities || activities.length === 0) {
        return (
            <LoadingWrapper isLoading={isLoading}>
                <div className="text-center py-3">
                    <p className="text-muted mb-0">No activity yet</p>
                </div>
            </LoadingWrapper>
        )
    }

    return (
        <LoadingWrapper isLoading={isLoading}>
            <div>
                {activities.map((activity) => (
                    <div key={activity.id} className="mb-3 pb-3 border-bottom">
                        <div className="mb-1">
                            <strong>{activity.userName}</strong> {getEventDescription(activity)} {activity.itemType}: <strong>{activity.itemName}</strong>
                        </div>
                        {activity.changes && Object.keys(activity.changes).length > 0 && (
                            <ul className="mb-1 text-muted ps-3">
                                {formatChanges(activity.changes, activity.event)}
                            </ul>
                        )}
                        <small className="text-muted">
                            {moment(activity.createdAt).fromNow()}
                        </small>
                    </div>
                ))}
            </div>

            {pagy && pagy.pages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 pt-3">
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={!pagy.prev || isLoading}
                        onClick={() => setCurrentPage(pagy.prev!)}
                    >
                        ← Previous
                    </button>
                    <span className="text-muted font-13">
                        Page {pagy.page} of {pagy.pages} ({pagy.count} total)
                    </span>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={!pagy.next || isLoading}
                        onClick={() => setCurrentPage(pagy.next!)}
                    >
                        Next →
                    </button>
                </div>
            )}
        </LoadingWrapper>
    )
}

export default ActivityFeed
